import { WorkshopDocument } from '../entities/WorkshopDocument.js';
import { map, of } from 'rxjs';
import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';

export const getWorkshopDocumentsByIDs = registerQuery({
  operationName: 'getWorkshopDocumentsByIDs',
  type: () => [WorkshopDocument],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<WorkshopDocument>>(gqlFields: SelSet) =>
      readEntitiesByIDs(WorkshopDocument, ids, gqlFields),
});

export const subscribeWorkshopDocumentsByIDs = registerSubscription({
  operationName: 'subscribeWorkshopDocumentsByIDs',
  type: () => [WorkshopDocument],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<WorkshopDocument>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(WorkshopDocument, ids, gqlFields),
});

export const getAllWorkshopDocuments = registerQuery({
  operationName: 'getAllWorkshopDocuments',
  type: () => [WorkshopDocument],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<WorkshopDocument>>(gqlFields: SelSet) =>
      readAllEntities(WorkshopDocument, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllWorkshopDocuments = registerSubscription({
  operationName: 'subscribeAllWorkshopDocuments',
  type: () => [WorkshopDocument],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<WorkshopDocument>>(gqlFields: SelSet) =>
      watchAllEntities(WorkshopDocument, gqlFields, { skip, limit, excludeIDs }),
});

export const getWorkshopDocumentByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<WorkshopDocument>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getWorkshopDocumentsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeWorkshopDocumentByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<WorkshopDocument>>(gqlFields: SelSet) =>
    id ? subscribeWorkshopDocumentsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
